import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, ArrowDown, ArrowUp, Minus, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAgencyDashboardAccess } from "@/hooks/use-agency-dashboard-access";

interface SubScore<R = Record<string, unknown>> {
  score: number | null;
  reason?: string;
  raw: R;
}

interface TalentRow {
  talentId: string;
  name: string;
  photoUrl: string | null;
  composite: number | null;
  priorComposite: number | null;
  trend: "up" | "down" | "flat" | "unknown";
  subScores: {
    velocity: SubScore<{ current: number; prior: number; windowDays: number }>;
    churn: SubScore<{
      clientsConsidered: number;
      avgChurnScore: number | null;
      topReasons: string[];
    }>;
    payment: SubScore<{ paidInvoices: number; avgDaysLate: number | null }>;
    social: SubScore<{
      posts: number;
      impressions: number;
      engagement: number;
      engagementRate: number | null;
    }>;
  };
  contributions: {
    velocity: number;
    churn: number;
    payment: number;
    social: number;
  };
}

interface RosterHealthResponse {
  windowDays: number;
  weights: { velocity: number; churn: number; payment: number; social: number };
  summary: {
    activeTalents: number;
    averageScore: number | null;
    trending: { up: number; down: number; flat: number; unknown: number };
  };
  rows: TalentRow[];
}

type SortKey =
  | "name"
  | "composite"
  | "velocity"
  | "churn"
  | "payment"
  | "social";

function scoreClass(score: number | null): string {
  if (score === null) return "text-muted-foreground";
  if (score >= 75) return "text-emerald-600 dark:text-emerald-400 font-semibold";
  if (score >= 50) return "text-amber-600 dark:text-amber-400 font-semibold";
  return "text-rose-600 dark:text-rose-400 font-semibold";
}

function fmt(score: number | null): string {
  return score === null ? "—" : Math.round(score).toString();
}

function TrendIcon({ trend }: { trend: TalentRow["trend"] }) {
  if (trend === "up")
    return <ArrowUp className="inline h-3.5 w-3.5 text-emerald-500" data-testid="icon-trend-up" />;
  if (trend === "down")
    return <ArrowDown className="inline h-3.5 w-3.5 text-rose-500" data-testid="icon-trend-down" />;
  if (trend === "flat")
    return <Minus className="inline h-3.5 w-3.5 text-muted-foreground" data-testid="icon-trend-flat" />;
  return <span className="inline-block w-3.5" />;
}

export default function AgencyDashboardPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const access = useAgencyDashboardAccess();
  const [windowDays, setWindowDays] = useState<30 | 90 | 180>(90);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("composite");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [weightDraft, setWeightDraft] = useState<{
    velocity: number;
    churn: number;
    payment: number;
    social: number;
  } | null>(null);

  const queryKey = ["/api/agency/roster-health", windowDays] as const;
  const dashboard = useQuery<RosterHealthResponse>({
    queryKey,
    enabled: access.data?.enabled === true,
    queryFn: async () => {
      const res = await fetch(
        `/api/agency/roster-health?windowDays=${windowDays}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  const saveWeights = useMutation({
    mutationFn: async (weights: {
      velocity: number;
      churn: number;
      payment: number;
      social: number;
    }) => {
      const res = await apiRequest("PATCH", "/api/agency/weights", weights);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agency/roster-health"] });
      setWeightDraft(null);
      toast({ title: "Score weights saved" });
    },
    onError: (err: any) => {
      toast({
        title: "Couldn't save weights",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      });
    },
  });

  // Access gate — when disabled the page renders a 404-style message
  // (matches the server returning 404). The nav entry never shows up
  // either, so this is a defense-in-depth fallback.
  if (access.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (access.data?.enabled !== true) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center" data-testid="agency-dashboard-not-found">
        <h1 className="text-2xl font-semibold mb-2">Page not found</h1>
        <p className="text-muted-foreground mb-4">
          The page you're looking for doesn't exist.
        </p>
        <Button onClick={() => navigate("/")} data-testid="button-back-home">
          Back to dashboard
        </Button>
      </div>
    );
  }

  const rows = dashboard.data?.rows ?? [];
  const filtered = useMemoFilteredSorted(rows, search, sortKey, sortDir);
  const summary = dashboard.data?.summary;
  const weights = weightDraft ?? dashboard.data?.weights ?? null;
  const selected = rows.find((r) => r.talentId === selectedId) ?? null;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto" data-testid="agency-dashboard-page">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="text-agency-title">
            Agency Dashboard
          </h1>
          <p className="text-sm text-muted-foreground">
            Per-talent Roster Health Score across your roster.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(windowDays)}
            onValueChange={(v) => setWindowDays(Number(v) as 30 | 90 | 180)}
          >
            <SelectTrigger className="w-40" data-testid="select-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 180 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Summary strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-strip">
        <SummaryCard
          label="Active talents"
          value={summary?.activeTalents ?? 0}
          testId="summary-active"
        />
        <SummaryCard
          label="Avg health score"
          value={summary?.averageScore == null ? "—" : Math.round(summary.averageScore)}
          accent={scoreClass(summary?.averageScore ?? null)}
          testId="summary-average"
        />
        <SummaryCard
          label="Trending up"
          value={summary?.trending.up ?? 0}
          accent="text-emerald-600 dark:text-emerald-400"
          testId="summary-up"
        />
        <SummaryCard
          label="Trending down"
          value={summary?.trending.down ?? 0}
          accent="text-rose-600 dark:text-rose-400"
          testId="summary-down"
        />
      </section>

      {/* Score weights editor */}
      {weights && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">Score weights</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            {(
              [
                ["velocity", "Velocity"],
                ["churn", "Churn"],
                ["payment", "Payment"],
                ["social", "Social"],
              ] as const
            ).map(([key, label]) => (
              <div key={key}>
                <label className="text-xs text-muted-foreground mb-1 block">
                  {label}
                </label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={weights[key]}
                  onChange={(e) => {
                    const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                    setWeightDraft({ ...weights, [key]: v });
                  }}
                  data-testid={`input-weight-${key}`}
                />
              </div>
            ))}
            <Button
              onClick={() => weightDraft && saveWeights.mutate(weightDraft)}
              disabled={!weightDraft || saveWeights.isPending}
              data-testid="button-save-weights"
            >
              {saveWeights.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1" /> Save
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="py-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Roster</CardTitle>
          <Input
            placeholder="Search talent…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
            data-testid="input-search-talent"
          />
        </CardHeader>
        <CardContent>
          {dashboard.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No active talent yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHeader sortKey="name" current={sortKey} dir={sortDir} onClick={(k) => { setSortKey(k); setSortDir(sortKey === k && sortDir === "desc" ? "asc" : "desc"); }}>Talent</SortHeader>
                  <SortHeader sortKey="composite" current={sortKey} dir={sortDir} onClick={(k) => { setSortKey(k); setSortDir(sortKey === k && sortDir === "desc" ? "asc" : "desc"); }}>Score</SortHeader>
                  <SortHeader sortKey="velocity" current={sortKey} dir={sortDir} onClick={(k) => { setSortKey(k); setSortDir(sortKey === k && sortDir === "desc" ? "asc" : "desc"); }}>Velocity</SortHeader>
                  <SortHeader sortKey="churn" current={sortKey} dir={sortDir} onClick={(k) => { setSortKey(k); setSortDir(sortKey === k && sortDir === "desc" ? "asc" : "desc"); }}>Churn</SortHeader>
                  <SortHeader sortKey="payment" current={sortKey} dir={sortDir} onClick={(k) => { setSortKey(k); setSortDir(sortKey === k && sortDir === "desc" ? "asc" : "desc"); }}>Payment</SortHeader>
                  <SortHeader sortKey="social" current={sortKey} dir={sortDir} onClick={(k) => { setSortKey(k); setSortDir(sortKey === k && sortDir === "desc" ? "asc" : "desc"); }}>Social</SortHeader>
                  <TableHead>Trend</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((row) => (
                  <TableRow
                    key={row.talentId}
                    className="cursor-pointer hover-elevate"
                    onClick={() => setSelectedId(row.talentId)}
                    data-testid={`row-talent-${row.talentId}`}
                  >
                    <TableCell className="flex items-center gap-2">
                      <Avatar className="h-7 w-7">
                        <AvatarImage src={row.photoUrl ?? undefined} />
                        <AvatarFallback>{row.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{row.name}</span>
                    </TableCell>
                    <TableCell className={scoreClass(row.composite)} data-testid={`text-composite-${row.talentId}`}>
                      {fmt(row.composite)}
                    </TableCell>
                    <TableCell className={scoreClass(row.subScores.velocity.score)}>{fmt(row.subScores.velocity.score)}</TableCell>
                    <TableCell className={scoreClass(row.subScores.churn.score)}>{fmt(row.subScores.churn.score)}</TableCell>
                    <TableCell className={scoreClass(row.subScores.payment.score)}>{fmt(row.subScores.payment.score)}</TableCell>
                    <TableCell className={scoreClass(row.subScores.social.score)}>{fmt(row.subScores.social.score)}</TableCell>
                    <TableCell>
                      <TrendIcon trend={row.trend} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Drill-down panel */}
      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelectedId(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="drilldown-panel">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle>{selected.name}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Composite</div>
                  <div className={`text-3xl ${scoreClass(selected.composite)}`} data-testid="drilldown-composite">
                    {fmt(selected.composite)}
                    <Badge variant="outline" className="ml-2 align-middle text-xs">
                      <TrendIcon trend={selected.trend} /> vs prior
                    </Badge>
                  </div>
                </div>
                <DrilldownRow
                  label="Bookings velocity"
                  score={selected.subScores.velocity.score}
                  reason={selected.subScores.velocity.reason}
                  contribution={selected.contributions.velocity}
                  raw={`${selected.subScores.velocity.raw.current} this window vs ${selected.subScores.velocity.raw.prior} prior`}
                />
                <DrilldownRow
                  label="Churn risk (inverted)"
                  score={selected.subScores.churn.score}
                  reason={selected.subScores.churn.reason}
                  contribution={selected.contributions.churn}
                  raw={
                    selected.subScores.churn.raw.clientsConsidered === 0
                      ? "no clients"
                      : `${selected.subScores.churn.raw.clientsConsidered} clients · top reasons: ${selected.subScores.churn.raw.topReasons.join(", ") || "—"}`
                  }
                />
                <DrilldownRow
                  label="Payment timeliness"
                  score={selected.subScores.payment.score}
                  reason={selected.subScores.payment.reason}
                  contribution={selected.contributions.payment}
                  raw={
                    selected.subScores.payment.raw.paidInvoices === 0
                      ? "no paid invoices"
                      : `${selected.subScores.payment.raw.paidInvoices} paid · avg ${(selected.subScores.payment.raw.avgDaysLate ?? 0).toFixed(1)} days late`
                  }
                />
                <DrilldownRow
                  label="Social engagement"
                  score={selected.subScores.social.score}
                  reason={selected.subScores.social.reason}
                  contribution={selected.contributions.social}
                  raw={
                    selected.subScores.social.raw.posts === 0
                      ? "no posts"
                      : `${selected.subScores.social.raw.posts} posts · ${(((selected.subScores.social.raw.engagementRate ?? 0) * 100).toFixed(2))}% engagement`
                  }
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  accent,
  testId,
}: {
  label: string;
  value: number | string;
  accent?: string;
  testId: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase text-muted-foreground">{label}</div>
        <div className={`text-2xl font-semibold ${accent ?? ""}`} data-testid={testId}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function SortHeader({
  sortKey,
  current,
  dir,
  onClick,
  children,
}: {
  sortKey: SortKey;
  current: SortKey;
  dir: "asc" | "desc";
  onClick: (k: SortKey) => void;
  children: React.ReactNode;
}) {
  const active = sortKey === current;
  return (
    <TableHead
      className="cursor-pointer select-none"
      onClick={() => onClick(sortKey)}
      data-testid={`sort-${sortKey}`}
    >
      {children}
      {active ? (dir === "desc" ? " ↓" : " ↑") : ""}
    </TableHead>
  );
}

function DrilldownRow({
  label,
  score,
  reason,
  contribution,
  raw,
}: {
  label: string;
  score: number | null;
  reason?: string;
  contribution: number;
  raw: string;
}) {
  return (
    <div className="border-t pt-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span className={`text-sm ${scoreClass(score)}`}>
          {fmt(score)}
          {score !== null && (
            <span className="text-xs text-muted-foreground ml-2">
              (+{contribution.toFixed(1)} pts)
            </span>
          )}
        </span>
      </div>
      <div className="text-xs text-muted-foreground mt-1">
        {score === null ? `No data — ${reason ?? "n/a"}` : raw}
      </div>
    </div>
  );
}

function useMemoFilteredSorted(
  rows: TalentRow[],
  search: string,
  sortKey: SortKey,
  dir: "asc" | "desc",
): TalentRow[] {
  return useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) => r.name.toLowerCase().includes(q))
      : rows.slice();
    const get = (r: TalentRow): number | string => {
      switch (sortKey) {
        case "name":
          return r.name.toLowerCase();
        case "composite":
          return r.composite ?? -1;
        case "velocity":
          return r.subScores.velocity.score ?? -1;
        case "churn":
          return r.subScores.churn.score ?? -1;
        case "payment":
          return r.subScores.payment.score ?? -1;
        case "social":
          return r.subScores.social.score ?? -1;
      }
    };
    filtered.sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [rows, search, sortKey, dir]);
}
